import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useModal } from '../../../contexts/ModalContext';

export default function Hero() {
  const { openModal } = useModal();
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (bgRef.current) {
        bgRef.current.style.transform = `translateY(${window.scrollY * 0.4}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative h-screen min-h-[700px] flex items-end overflow-hidden bg-dark-ink">
      {/* Parallax BG */}
      <div
        ref={bgRef}
        className="absolute inset-0 bg-cover bg-top will-change-transform"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/9c3e23bc5b596ec599fb997e22ca1558.jpg)',
          top: '-15%',
          height: '130%',
        }}
      />

      {/* Multi-layer overlays for depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-dark-ink/90 via-dark-ink/40 to-dark-ink/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-dark-ink/70 via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-8 lg:px-16 pb-28">
        {/* Eyebrow */}
        <div className="flex items-center gap-4 mb-8" style={{ animation: 'heroUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.1s both' }}>
          <div className="w-8 h-px bg-bronze" />
          <span className="font-sans text-[10px] tracking-[0.24em] uppercase text-white/45">
            Talo Architect, P.C. · Scarsdale, NY · Est. 2002
          </span>
        </div>

        {/* H1 */}
        <h1
          className="font-serif text-6xl sm:text-7xl lg:text-[88px] xl:text-[100px] font-light text-white leading-[1.0] mb-6 max-w-4xl"
          style={{ animation: 'heroUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.25s both' }}
        >
          Architecture That<br />
          <em className="not-italic text-white/70">Defines</em> How You Live
        </h1>

        {/* Subtitle */}
        <p
          className="font-sans text-base text-white/55 leading-relaxed mb-12 max-w-md"
          style={{ animation: 'heroUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.45s both' }}
        >
          High-end residential architecture for custom homes, renovations, and additions — designed with precision, purpose, and enduring form.
        </p>

        {/* CTAs */}
        <div
          className="flex flex-col sm:flex-row items-start gap-4"
          style={{ animation: 'heroUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.6s both' }}
        >
          <button
            onClick={openModal}
            className="font-sans text-[10px] tracking-[0.22em] uppercase px-10 py-4 bg-white text-dark-ink hover:bg-soft-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Schedule Consultation
          </button>
          <Link
            to="/services"
            className="font-sans text-[10px] tracking-[0.22em] uppercase px-10 py-4 border border-white/25 text-white hover:bg-white/10 transition-colors duration-300 cursor-pointer whitespace-nowrap text-center"
          >
            View Our Services
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-10 right-16 flex items-center gap-3"
        style={{ animation: 'heroUp 1s ease 1.3s both' }}
      >
        <span className="font-sans text-[10px] tracking-[0.22em] uppercase text-white/30">Explore</span>
        <div className="w-10 h-px bg-white/20" />
        <div className="w-2 h-2 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/30 text-xs" />
        </div>
      </div>

      {/* Award badge */}
      <div
        className="absolute top-1/2 right-12 -translate-y-1/2 hidden xl:flex flex-col items-center gap-2"
        style={{ animation: 'heroUp 1s ease 1s both' }}
      >
        <div className="border border-white/15 p-5 flex flex-col items-center gap-1">
          <span className="font-serif text-3xl font-light text-white/60">20+</span>
          <span className="font-sans text-[9px] tracking-[0.2em] uppercase text-white/30">Years</span>
          <div className="w-4 h-px bg-white/20 my-1" />
          <span className="font-sans text-[9px] tracking-[0.2em] uppercase text-white/30">NYC &amp; Westchester</span>
        </div>
      </div>

      <style>{`
        @keyframes heroUp {
          from { opacity: 0; transform: translateY(36px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
